import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Box, makeStyles } from '@material-ui/core';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';

import { ActionButton, KlpModal, IconName, Illustration } from '@klp/design';
import { useStartProsess } from '../LeverandorActions/useStartProcess';
import { LeverandorRoutes } from '../../config';
import { RegionSelect } from './Form/RegionSelect';
import { OrganizationSelect } from './Form/OrganizationSelect';
import { ManualOrganizationEntry } from './Form/ManualOrganizationEntry';
import { SuperOfficeContactSelect } from './Form/SuperOfficeContactSelect';
import { ExistingProcessCheck } from './Form/ExistingProcessCheck';

import {
    COUNTRY_WITH_API_SUPPORT,
    type LEVERANDOR_REGIONS,
    type LeverandorGodkjenningProsessResponse,
} from '@klp/leverandor/types';
import { useCurrentUser } from '@klp/auth';
import {
    mapRegionToCountryOption,
    isCountryWithApiSupport,
} from '@klp/leverandor/utils';
import { LeverandorTypeSelect } from './Form/LeverandorTypeSelect';

const useStyles = makeStyles((theme) => ({
    buttonContainer: {
        display: 'flex',
        justifyContent: 'space-between',
        marginTop: theme.spacing(2),
    },
    modal: {
        maxWidth: 600,
    },
    horizontalMargin: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(2),
    },
}));

const createValidationSchema = () => {
    return Yup.object().shape({
        type: Yup.string().required(
            'leverandor.createLeverandor.validation.typeRequired',
        ),
        region: Yup.string()
            .oneOf([
                'Oslo',
                'Trondheim',
                'Sverige',
                'Danmark',
                'Europa',
            ] as const)
            .required('leverandor.createLeverandor.validation.regionRequired'),
        orgnummer: Yup.string()
            .required(
                'leverandor.createLeverandor.validation.orgnummerRequired',
            )
            .matches(
                /^[0-9]+$/,
                'leverandor.createLeverandor.validation.orgnummerDigits',
            ),
        country: Yup.string().required(
            'leverandor.createLeverandor.validation.countryRequired',
        ),
        leverandorName: Yup.string().nullable(),
        superOfficeContactId: Yup.string().nullable(),
    });
};

export type CreateLeverandorFormValues = {
    region: LEVERANDOR_REGIONS | undefined;
    country: string;
    soCountryId: string | undefined;
    orgnummer: string | undefined;
    leverandorName: string | undefined;
    type: string | undefined;
    superOfficeContactId: string | undefined;
    culture?: string;
};

export const CreateLeverandor = () => {
    const classes = useStyles();
    const { t } = useTranslation();
    const { currentUser } = useCurrentUser();
    const history = useHistory();
    const [open, setOpen] = useState(false);
    const [existingProcess, setExistingProcess] =
        useState<LeverandorGodkjenningProsessResponse | null>(null);
    const { startProcess, isLoading: createLeverandorIsLoading } =
        useStartProsess();

    const initialRegion = currentUser?.region as LEVERANDOR_REGIONS | undefined;
    const initialCountry =
        mapRegionToCountryOption(initialRegion) ??
        COUNTRY_WITH_API_SUPPORT.Norway;
    const initialValues: CreateLeverandorFormValues = {
        region: initialRegion,
        country: initialCountry,
        soCountryId: undefined,
        orgnummer: undefined,
        leverandorName: undefined,
        superOfficeContactId: undefined,
        type: undefined, // Default type as "Fastleverandør" for now, since type selection is commented out and we await further clarification.
    };

    const handleSubmit = async (values: CreateLeverandorFormValues) => {
        if (
            !values.orgnummer ||
            !values.region ||
            !values.country ||
            !values.type
        ) {
            return;
        }
        if (existingProcess?.prosessInstansId) {
            history.push(
                LeverandorRoutes.leverandorSchemaSingle(
                    existingProcess.prosessInstansId,
                ),
            );
            handleClose();
            return;
        }

        startProcess({
            request: {
                virksomhet: {
                    organisasjonsnummer: values.orgnummer,
                    navn: values.leverandorName,
                    land: values.country,
                    SOCountryId: values.soCountryId,
                },
                region: values.region,
                superOfficeContactId: values.superOfficeContactId,
                type: values.type,
            },
            onClose: handleClose,
        });
    };

    const handleOpen = () => setOpen(true);
    const handleClose = () => {
        setOpen(false);
    };

    return (
        <Box>
            <Button
                endIcon={
                    <Illustration icon={IconName.PLUS} klpPalette="secondary" />
                }
                variant="contained"
                color="secondary"
                onClick={handleOpen}
            >
                {t('leverandor.createLeverandor.button')}
            </Button>
            <KlpModal
                isOpen={open}
                onClose={handleClose}
                translation="leverandor.createLeverandor.title"
                className={classes.modal}
            >
                <Formik<CreateLeverandorFormValues>
                    initialValues={initialValues}
                    onSubmit={handleSubmit}
                    validationSchema={createValidationSchema}
                >
                    {({ values, touched, errors, setFieldValue }) => (
                        <Form>
                            <LeverandorTypeSelect
                                value={values.type}
                                touched={touched.type}
                                error={errors.type}
                                onChange={(type) => {
                                    setFieldValue('type', type);
                                }}
                            />

                            <Box className={classes.horizontalMargin}>
                                <RegionSelect
                                    value={values.region}
                                    touched={touched.region}
                                    error={errors.region}
                                    onChange={(region) => {
                                        setFieldValue('region', region);
                                    }}
                                />
                            </Box>

                            <Box className={classes.horizontalMargin}>
                                {isCountryWithApiSupport(values.country) ? (
                                    <OrganizationSelect
                                        initialCountry={values.country}
                                        touched={touched.orgnummer}
                                        onChange={(orgnummer) => {
                                            setFieldValue(
                                                'orgnummer',
                                                orgnummer,
                                            );
                                        }}
                                        onCountryChange={(
                                            country,
                                            soCountryId,
                                        ) => {
                                            setFieldValue('country', country);
                                            setFieldValue(
                                                'soCountryId',
                                                soCountryId,
                                            );
                                        }}
                                        onNameChange={(name) => {
                                            setFieldValue(
                                                'leverandorName',
                                                name,
                                            );
                                        }}
                                    />
                                ) : (
                                    <ManualOrganizationEntry
                                        onChange={(orgnummer) => {
                                            setFieldValue(
                                                'orgnummer',
                                                orgnummer,
                                            );
                                        }}
                                        onCountryChange={(
                                            country,
                                            soCountryId,
                                        ) => {
                                            setFieldValue('country', country);
                                            setFieldValue(
                                                'soCountryId',
                                                soCountryId,
                                            );
                                        }}
                                        onNameChange={(name) => {
                                            setFieldValue(
                                                'leverandorName',
                                                name,
                                            );
                                        }}
                                    />
                                )}
                            </Box>

                            {values.type &&
                                values.region &&
                                values.orgnummer && (
                                    <Box className={classes.horizontalMargin}>
                                        <ExistingProcessCheck
                                            orgnummer={values.orgnummer}
                                            region={values.region}
                                            superOfficeId={
                                                values.superOfficeContactId
                                            }
                                            setExistingProcess={
                                                setExistingProcess
                                            }
                                        />
                                        <SuperOfficeContactSelect
                                            orgnummer={values.orgnummer}
                                            touched={
                                                touched.superOfficeContactId
                                            }
                                            error={errors.superOfficeContactId}
                                            onChange={(contactId) => {
                                                setFieldValue(
                                                    'superOfficeContactId',
                                                    contactId,
                                                );
                                            }}
                                        />
                                    </Box>
                                )}

                            <Box className={classes.buttonContainer}>
                                <ActionButton
                                    translation={
                                        existingProcess
                                            ? t(
                                                  'leverandor.createLeverandor.navigateToSchema',
                                              )
                                            : t(
                                                  'leverandor.createLeverandor.button',
                                              )
                                    }
                                    actionType="submit"
                                    variant="contained"
                                    color="secondary"
                                    type="submit"
                                    isLoading={createLeverandorIsLoading}
                                />
                            </Box>
                        </Form>
                    )}
                </Formik>
            </KlpModal>
        </Box>
    );
};
